import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api';

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const verified = searchParams.get('verified') === '1';

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
         style={{ background: '#1e3a5f' }}>

      <div className="w-full max-w-sm">

        {/* Logo + brand */}
        <div className="flex flex-col items-center mb-8">
          <img
            src="/logo.png?v=4"
            alt="VantixMT"
            className="w-40 sm:w-56 object-contain drop-shadow-xl"
          />
        </div>

        {verified && (
          <div className="mb-4 flex items-center gap-2 bg-green-900/40 border border-green-500/40 text-green-300 text-sm rounded-xl px-4 py-3">
            <CheckCircle size={16} className="flex-shrink-0" />
            Email verified! You can now sign in.
          </div>
        )}

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl px-8 py-8">
          <h2 className="text-lg font-semibold text-slate-800 mb-6">Sign in</h2>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <Input
              label="Email"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoFocus
            />
            <Input
              label="Password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />

            <div className="flex justify-end -mt-1">
              <Link to="/forgot-password" className="text-xs text-brand-600 hover:underline">
                Forgot password?
              </Link>
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>
            )}

            <Button type="submit" className="w-full mt-1" disabled={loading}>
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </div>

        {/* Footer links */}
        <p className="text-center text-sm text-brand-200 mt-5">
          New company?{' '}
          <Link to="/register" className="text-white font-medium hover:underline">
            Create an account
          </Link>
        </p>
        <p className="text-center text-xs text-brand-200/80 mt-3">
          VantixMT is a product of Shelton Tech Solutions LLC
        </p>
      </div>
    </div>
  );
}
